<template>
  <div class="login-page">
    <!-- <DesenvolvimentoAvisoImportante /> -->

    <!-- Login Form -->
    <div class="login-form-container" v-if="!forcePasswordChange && !isLoading">
      <el-card class="login-card">
        <h2 class="service-name">Sistema De Marcação de Sepultamentos (CEMERP)</h2>
        <el-form
            :model="form"
            :rules="rules"
            ref="loginFormRef"
            label-position="top"
            class="login-form"
        >
          <el-form-item label="Email" prop="email">
            <el-input v-model="form.email" placeholder="Insira seu e-mail" />
          </el-form-item>
          <el-form-item label="Senha" prop="password">
            <!-- O atributo show-password adiciona o botão toggle para mostrar/ocultar a senha -->
            <el-input
                type="password"
                v-model="form.password"
                placeholder="Insira sua senha"
                show-password
            />
          </el-form-item>
          <el-button
              type="primary"
              @click="onSubmit"
              :loading="isLoading"
              class="login-button"
          >
            Entrar
          </el-button>
        </el-form>
      </el-card>
    </div>

    <!-- Forced Password Change Form -->
    <div class="login-form-container" v-if="forcePasswordChange && !isPasswordChanging">
      <el-card class="login-card">
        <h2 class="service-name">Alterar Senha</h2>
        <el-form
            :model="passwordForm"
            :rules="passwordRules"
            ref="passwordFormRef"
            label-position="top"
            class="login-form"
        >
          <el-form-item label="Senha Atual" prop="oldPassword">
            <el-input
                type="password"
                v-model="passwordForm.oldPassword"
                placeholder="Digite sua senha atual"
                show-password
            />
          </el-form-item>
          <el-form-item label="Nova Senha" prop="newPassword">
            <el-input
                type="password"
                v-model="passwordForm.newPassword"
                placeholder="Digite a nova senha"
                show-password
            />
          </el-form-item>
          <el-form-item label="Confirmar Nova Senha" prop="confirmPassword">
            <el-input
                type="password"
                v-model="passwordForm.confirmPassword"
                placeholder="Confirme a nova senha"
                show-password
            />
          </el-form-item>
          <el-button
              type="primary"
              @click="onChangePassword"
              :loading="isPasswordChanging"
          >
            Alterar Senha
          </el-button>
        </el-form>
      </el-card>
    </div>

    <!-- Loading State -->
    <div class="loading-container" v-if="isLoading || isPasswordChanging">
      <el-icon class="loading-spinner">
        <i class="el-icon-loading"></i>
      </el-icon>
      <p class="loading-text">Gerando ambiente seguro...</p>
    </div>

    <footer class="login-footer">
      <div>
        <p class="security-message">
          Este sistema é monitorado e todas as ações são registradas para
          conformidade com a LGPD/ANPD. As suas credenciais de acesso são de sua
          inteira responsabilidade. Siga as melhores práticas de segurança abaixo:
        </p>
        <ul class="security-tips">
          <li>NUNCA compartilhe sua senha com terceiros.</li>
          <li>Ao encerrar suas atividades, faça logout.</li>
          <li>Evite acessar o sistema em dispositivos públicos ou desconhecidos.</li>
          <li>Não utilize redes de internet desprotegidas ou públicas.</li>
          <li>
            Tenha em mente que extensões de navegador podem ter acesso à todos os
            dados durante sua navegação em qualquer website; desative-as antes de
            acessar e recarregue a página.
          </li>
        </ul>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { ElMessage } from "element-plus";

export default {
  name: "LoginPage",
  components: {
  },
  setup() {
    const userStore = useUserStore();
    const router = useRouter();

    // Login form state and rules
    const form = ref({
      email: "",
      password: "",
    });
    const rules = ref({
      email: [
        { required: true, message: "Email é obrigatório", trigger: "blur" },
      ],
      password: [
        { required: true, message: "Senha é obrigatória", trigger: "blur" },
      ],
    });
    const loginFormRef = ref(null);
    const isLoading = ref(false);

    // Forced password change state and form
    const forcePasswordChange = ref(false);
    const passwordForm = ref({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    const passwordRules = ref({
      oldPassword: [
        { required: true, message: "Senha atual é obrigatória", trigger: "blur" },
      ],
      newPassword: [
        {
          required: true,
          message: "Nova senha é obrigatória",
          trigger: "blur",
        },
        {
          min: 6,
          message: "A nova senha deve ter no mínimo 6 caracteres",
          trigger: "blur",
        },
      ],
      confirmPassword: [
        { required: true, message: "Confirme a nova senha", trigger: "blur" },
        {
          validator: (rule, value, callback) => {
            if (value !== passwordForm.value.newPassword) {
              callback(new Error("As senhas não conferem"));
            } else {
              callback();
            }
          },
          trigger: "blur",
        },
      ],
    });
    const passwordFormRef = ref(null);
    const isPasswordChanging = ref(false);

    // Login submission logic
    const onSubmit = async () => {
      if (!form.value.email || !form.value.password) {
        ElMessage.error("Por favor, preencha o email e a senha.");
        return;
      }
      if (!loginFormRef.value) return;
      try {
        await loginFormRef.value.validate();
        isLoading.value = true;
        try {
          await userStore.login(form.value.email, form.value.password);
          // If the user's associated funeraria is blocked, show a popup message
          if (
              userStore.user &&
              userStore.user.funeraria &&
              userStore.user.funeraria.ativo === false
          ) {
            ElMessage.error(
                "Esta conta está suspensa! Entre em contato com a central!"
            );
            isLoading.value = false;
            userStore.logout();
            return;
          }
          // If first login, show password change form and do NOT redirect.
          if (userStore.user && userStore.user.primeiroLogin) {
            forcePasswordChange.value = true;
            passwordForm.value.oldPassword = form.value.password;
            // Do not redirect to dashboard
            return;
          }
          // Redirect based on user role.
          switch (userStore.role) {
            case "admin_prefeitura":
              await router.push("/dashboard/admin-prefeitura");
              break;
            case "prefeitura":
              await router.push("/dashboard/prefeitura");
              break;
            case "admin_cemiterio":
            case "cemiterio":
              await router.push("/dashboard/cemiterio");
              break;
            case "admin_funeraria":
            case "funeraria":
              await router.push("/dashboard/funeraria");
              break;
            default:
              await router.push("/unauthorized");
          }
        } catch (error) {
          // Extract message from error response if available
          const errorMsg =
              error.response?.data?.message ||
              error.message ||
              "Um Erro Ocorreu.";
          ElMessage.error(errorMsg);
          form.value.email = "";
          form.value.password = "";
        } finally {
          isLoading.value = false;
        }
      } catch (err) {
        console.error("Form validation failed", err);
      }
    };

    // Forced password change submission logic
    const onChangePassword = async () => {
      if (!passwordFormRef.value) return;
      try {
        await passwordFormRef.value.validate();
        isPasswordChanging.value = true;
        await userStore.changePassword(passwordForm.value);
        forcePasswordChange.value = false;
        switch (userStore.role) {
          case "admin_prefeitura":
            await router.replace("/dashboard/admin-prefeitura");
            break;
          case "prefeitura":
            await router.replace("/dashboard/prefeitura");
            break;
          case "admin_cemiterio":
          case "cemiterio":
            await router.replace("/dashboard/cemiterio");
            break;
          case "admin_funeraria":
          case "funeraria":
            await router.replace("/dashboard/funeraria");
            break;
          default:
            await router.replace("/unauthorized");
        }
      } catch (error) {
        const errorMsg =
            error.response?.data?.message ||
            error.message ||
            "Erro ao alterar a senha";
        ElMessage.error(errorMsg);
      } finally {
        isPasswordChanging.value = false;
      }
    };

    return {
      form,
      rules,
      loginFormRef,
      isLoading,
      onSubmit,
      forcePasswordChange,
      passwordForm,
      passwordRules,
      passwordFormRef,
      isPasswordChanging,
      onChangePassword,
    };
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: url('../assets/bandeira-sao-goncalo.png') center/cover no-repeat;
}

.login-form-container,
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  background: rgba(255, 255, 255, 0.8);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.login-card {
  width: 400px;
}

.service-name {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.security-message {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.security-tips {
  list-style: disc;
  padding-left: 1.5rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-spinner {
  margin-bottom: 1rem;
}

.loading-text {
  font-size: 1.2rem;
  font-weight: bold;
}

.login-footer {
  padding: 1rem 0;
  text-align: center;
  font-size: 0.9rem;
  color: #333;
  background-color: rgba(255, 255, 255, 0.8);
  border-top: 1px solid #ccc;
}
</style>
